import { createFileRoute, Link } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { Compass, Heart, MapPin, Ticket, UtensilsCrossed, X } from "lucide-react";
import { useFavorites } from "@/hooks/useFavorites";
import { usePublicPlaces } from "@/hooks/use-public-places";
import { useNearestSort } from "@/hooks/useNearestSort";
import { PlaceCard } from "@/components/PlaceCard";
import { useLanguage } from "@/i18n/LanguageContext";
import { selectSavedItems } from "@/lib/favorites";
import { REGION_ORDER, REGION_LABELS, type RegionKey } from "@/lib/region";

const INTER = '"Inter Variable", Inter, ui-sans-serif, system-ui, sans-serif';

export const Route = createFileRoute("/saved")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { r?: string; city?: string } => ({
    r: typeof search.r === "string" ? search.r : undefined,
    city: typeof search.city === "string" ? search.city : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Saved — what" },
      { name: "description", content: "Your saved places, experiences and events on what." },
    ],
  }),
  component: SavedPage,
});

function parseRegions(raw: string | undefined): RegionKey[] {
  if (!raw || raw === "all") return [];
  const set = new Set<RegionKey>();
  for (const part of raw.split(",")) {
    const k = part.trim().toLowerCase() as RegionKey;
    if ((REGION_ORDER as string[]).includes(k)) set.add(k);
  }
  return Array.from(set);
}

function encodeRegions(keys: RegionKey[]): string {
  if (keys.length === 0) return "all";
  return REGION_ORDER.filter((k) => keys.includes(k)).join(",");
}

function SavedPage() {
  const { favs } = useFavorites();
  const { t } = useLanguage();
  const items = usePublicPlaces();
  const savedRaw = selectSavedItems(favs, items);
  const { items: sortedSaved } = useNearestSort(savedRaw);

  const { r, city } = Route.useSearch();
  const navigate = Route.useNavigate();
  const selected = useMemo(() => parseRegions(r), [r]);

  // City filter options built from the currently saved items
  const cityOptions = useMemo(() => {
    const map = new Map<string, number>();
    for (const it of sortedSaved) {
      const c = (it as { city?: string | null }).city;
      if (!c) continue;
      map.set(c, (map.get(c) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .map(([slug, count]) => ({ slug, count }))
      .sort((a, b) => b.count - a.count);
  }, [sortedSaved]);

  const activeCity = city && cityOptions.some((c) => c.slug === city) ? city : "all";

  // Items after city filter — region counts are derived from this
  const cityFiltered = useMemo(() => {
    if (activeCity === "all") return sortedSaved;
    return sortedSaved.filter((it) => (it as { city?: string | null }).city === activeCity);
  }, [sortedSaved, activeCity]);

  const regionCounts = useMemo(() => {
    const c: Record<RegionKey, number> = {
      lapland: 0, lakeland: 0, archipelago: 0, coast: 0,
    };
    for (const it of cityFiltered) {
      const k = (it as { region?: string | null }).region as RegionKey | undefined;
      if (k && k in c) c[k] += 1;
    }
    return c;
  }, [cityFiltered]);

  // Prune region selections that no longer have items after a city switch
  useEffect(() => {
    if (selected.length === 0) return;
    const valid = selected.filter((k) => regionCounts[k] > 0);
    if (valid.length !== selected.length) {
      navigate({
        search: (prev: { r?: string; city?: string }) => ({
          ...prev,
          r: encodeRegions(valid),
        }),
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCity, regionCounts.lapland, regionCounts.lakeland, regionCounts.archipelago, regionCounts.coast]);

  const saved = useMemo(() => {
    if (selected.length === 0) return cityFiltered;
    const set = new Set<string>(selected);
    return cityFiltered.filter((it) => {
      const k = (it as { region?: string | null }).region;
      return typeof k === "string" && set.has(k);
    });
  }, [cityFiltered, selected]);

  const toggleRegion = (k: RegionKey) => {
    const next = selected.includes(k)
      ? selected.filter((x) => x !== k)
      : [...selected, k];
    navigate({
      search: (prev: { r?: string; city?: string }) => ({
        ...prev,
        r: encodeRegions(next),
      }),
      replace: true,
    });
  };

  const clearRegions = () =>
    navigate({
      search: (prev: { r?: string; city?: string }) => ({ ...prev, r: "all" }),
      replace: true,
    });

  const setCity = (slug: string) =>
    navigate({
      // Switching city resets region selections so counts stay honest
      search: (prev: { r?: string; city?: string }) => ({
        ...prev,
        city: slug === "all" ? undefined : slug,
        r: "all",
      }),
      replace: true,
    });

  const chip =
    "shrink-0 h-9 inline-flex items-center gap-2 rounded-full pl-3.5 pr-2 text-[12.5px] font-semibold leading-none whitespace-nowrap transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-navy/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white";
  const chipOn = "bg-navy text-white shadow-[0_6px_16px_-8px_rgb(0_0_0/0.55)] ring-1 ring-navy";
  const chipOff = "bg-white text-navy/80 ring-1 ring-navy/12 hover:text-navy hover:ring-navy/30 hover:shadow-[0_4px_12px_-8px_rgb(0_0_0/0.4)] active:scale-[0.98]";
  const chipOffDisabled = "bg-navy/[0.03] text-navy/30 ring-1 ring-navy/[0.06] cursor-not-allowed";
  const countOn = "min-w-[20px] h-[20px] px-1.5 rounded-full bg-white/20 text-[10px] font-bold tabular-nums grid place-items-center";
  const countOff = "min-w-[20px] h-[20px] px-1.5 rounded-full bg-navy/[0.06] text-[10px] font-bold tabular-nums text-navy/55 grid place-items-center";


  return (
    <div className="min-h-[60vh] bg-[#eef1f6] text-navy not-italic" style={{ fontFamily: INTER }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-16">
        <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex items-start gap-3">
            <span className="mt-0.5 size-10 grid place-items-center rounded-2xl bg-white text-navy shadow-sm ring-1 ring-navy/10 shrink-0">
              <Heart className="size-4" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/45">
                {t("saved.eyebrow")}
              </p>
              <h1 className="mt-0.5 text-[22px] sm:text-[28px] font-semibold tracking-tight leading-tight not-italic">
                {t("saved.title")}
              </h1>
            </div>
          </div>
          {sortedSaved.length > 0 && (
            <span className="inline-flex h-8 items-center rounded-full bg-white px-3 text-[12px] font-semibold tabular-nums text-navy/70 ring-1 ring-navy/10">
              {saved.length === sortedSaved.length
                ? `${sortedSaved.length} ${sortedSaved.length === 1 ? "place" : "places"}`
                : `${saved.length} of ${sortedSaved.length}`}
            </span>
          )}
        </header>

        {sortedSaved.length > 0 && (
          <div className="mb-5 rounded-2xl border border-navy/10 bg-white/90 p-2.5 shadow-sm space-y-2">
            {cityOptions.length > 1 && (
              <div
                className="flex gap-2 overflow-x-auto no-scrollbar snap-x py-1 -my-1 sm:flex-wrap"
                role="group"
                aria-label="Filter by city"
              >
                <button
                  type="button"
                  onClick={() => setCity("all")}
                  aria-pressed={activeCity === "all"}
                  className={`${chip} ${activeCity === "all" ? chipOn : chipOff}`}
                >
                  <MapPin className="size-3.5 shrink-0" strokeWidth={2} />
                  All cities
                  <span className={activeCity === "all" ? countOn : countOff}>{sortedSaved.length}</span>
                </button>
                {cityOptions.map(({ slug, count }) => {
                  const active = activeCity === slug;
                  return (
                    <button
                      key={slug}
                      type="button"
                      onClick={() => setCity(slug)}
                      aria-pressed={active}
                      className={`${chip} capitalize ${active ? chipOn : chipOff}`}
                    >
                      {slug.replace(/-/g, " ")}
                      <span className={active ? countOn : countOff}>{count}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div
              className="flex gap-2 overflow-x-auto no-scrollbar snap-x py-1 -my-1 sm:flex-wrap"
              role="group"
              aria-label="Filter by travel region (multi-select)"
            >
              <button
                type="button"
                onClick={clearRegions}
                aria-pressed={selected.length === 0}
                className={`${chip} ${selected.length === 0 ? chipOn : chipOff}`}
              >
                All regions
                <span className={selected.length === 0 ? countOn : countOff}>{cityFiltered.length}</span>
              </button>
              {REGION_ORDER.map((k) => {
                const active = selected.includes(k);
                const count = regionCounts[k];
                const disabled = count === 0 && !active;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => !disabled && toggleRegion(k)}
                    disabled={disabled}
                    aria-pressed={active}
                    className={`${chip} ${active ? chipOn : disabled ? chipOffDisabled : chipOff}`}
                  >
                    {REGION_LABELS[k]}
                    <span className={active ? countOn : countOff}>{count}</span>
                  </button>
                );
              })}
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={clearRegions}
                  className="shrink-0 h-8 inline-flex items-center gap-1 rounded-full px-2.5 text-[12px] font-semibold text-navy/60 hover:text-navy"
                >
                  <X className="size-3.5" /> Clear
                </button>
              )}
            </div>
          </div>
        )}

        {sortedSaved.length === 0 ? (
          <div className="rounded-2xl border border-navy/10 bg-white px-6 py-14 text-center shadow-sm">
            <span className="mx-auto mb-4 size-12 grid place-items-center rounded-2xl bg-navy/[0.06] text-navy/50">
              <Heart className="size-5" strokeWidth={1.85} />
            </span>
            <p className="text-[18px] font-semibold tracking-tight not-italic">{t("saved.empty")}</p>
            <p className="mx-auto mt-1.5 max-w-sm text-[13px] text-navy/55 leading-snug">
              {t("saved.emptyBody")}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <Link
                to="/eat"
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-navy px-3.5 text-[13px] font-semibold text-white hover:bg-royal"
              >
                <UtensilsCrossed className="size-3.5" /> Eat
              </Link>
              <Link
                to="/do"
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-3.5 text-[13px] font-semibold text-navy ring-1 ring-navy/10 hover:ring-navy/25"
              >
                <Compass className="size-3.5" /> Do
              </Link>
              <Link
                to="/happening"
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-3.5 text-[13px] font-semibold text-navy ring-1 ring-navy/10 hover:ring-navy/25"
              >
                <Ticket className="size-3.5" /> Events
              </Link>
            </div>
          </div>
        ) : saved.length === 0 ? (
          <div className="rounded-2xl border border-navy/10 bg-white px-6 py-12 text-center shadow-sm">
            <p className="text-[16px] font-semibold tracking-tight not-italic">No saved places match these filters.</p>
            <p className="mt-1 text-[13px] text-navy/55">Try another city or region.</p>
            <button
              type="button"
              onClick={() => {
                clearRegions();
                setCity("all");
              }}
              className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-full bg-navy px-3.5 text-[13px] font-semibold text-white"
            >
              <X className="size-3.5" /> Reset filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {saved.map((it) => <PlaceCard key={it.id} item={it} showDistance />)}
          </div>
        )}
      </div>
    </div>
  );
}
